import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { StorageService } from './storage.service';
import { SignUpStorageKey } from 'src/app/_helpers/auth.constants';
import { jwtDecode } from 'jwt-decode';

const { AUTHORIZER_URL, USER_MANAGEMENT_URL, DEV_PORTAL_URL } = environment;
const httpOptions = {
  headers: new HttpHeaders(
    {
      'Content-Type': 'application/json',
      // returntype:"text"
    }


  )
};

interface JwtPayload {
  role_type: string;
}

@Injectable({
  providedIn: 'root' // Makes this service globally available
})
export class AuthService {
  private readonly protal_app: string = "DEVELOPER_PORTAL";
  

  constructor(private http: HttpClient, private router: Router, private storageService: StorageService) { }

  // Login API
  login(username: string, password: string): Observable<any> {
    return this.http.post(
      `${USER_MANAGEMENT_URL}/auth/login`,
      {
        "userName": username,
        "password": password,
        "app": this.protal_app
      },
      httpOptions
    );
  }

  newMenu(token: any): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + token,
    });
    return this.http.get(`${USER_MANAGEMENT_URL}/role/menu`, { headers });
  }

  signupRequest(data: any): Observable<any> {
    return this.http.post(
      `${USER_MANAGEMENT_URL}/developer-portal/signup`,
      {
        "fullName": data.fullName,
        "emailId": data.email,
        "companyName": data.company,
        "password": data.password,
        "phoneNumber":data.phoneNumber
      },
      httpOptions
    );
  }

  initEnvironment(data: any): Observable<any> {
    return this.http.post(
      `${USER_MANAGEMENT_URL}/developer-portal/init-env`,
      {
        "fullName": data.fullName,
        "emailId": data.emailId || data.email,
        "companyName": data.companyName || data.company,
        "password": data.password
      },
      httpOptions
    );
  }

  verifyOTP(otp: string, purpose: string = 'SIGNUP'): Observable<any> {
    const token = localStorage.getItem('otpToken');
    const emailId = localStorage.getItem('emailId') || localStorage.getItem('signupEmail');
    return this.http.post(
      `${USER_MANAGEMENT_URL}/auth/verify-otp`,
      {
        "emailId": emailId,
        "purpose": purpose.toUpperCase(),
        "otp": otp,
        "token": token,
        "app": this.protal_app
      },
      httpOptions
    );
  }

  resendOTP(purpose: string = 'SIGNUP'): Observable<any> {
    const emailId = localStorage.getItem('emailId') || localStorage.getItem('signupEmail');
    return this.http.post(
      `${USER_MANAGEMENT_URL}/auth/resend-otp`,
      {
        "emailId": emailId,
        "purpose": purpose.toUpperCase(),
        "app": this.protal_app
      },
      httpOptions
    );
  }

  updateSignupEmail(userId: string, signupData: {
    fullName: string;
    emailId: string;
    companyName: string;
    password: string;
  }): Observable<any> {
    const url = `${USER_MANAGEMENT_URL}/developer-portal/user/${userId}/signup`;
    const body = {
      fullName: signupData.fullName,
      emailId: signupData.emailId,
      companyName: signupData.companyName,
      password: signupData.password
    };
    console.log('PUT request to:', url, 'with body:', body);
    return this.http.put(
      url,
      body,
      httpOptions
    );
  }

  verifyEmail(emailId: string, purpose: string): Observable<any> {
    return this.http.post(
      USER_MANAGEMENT_URL + '/auth/verify-email',
      {
        "emailId": emailId,
        "purpose": purpose,
        "app": this.protal_app
      },
      httpOptions
    );
  }

  resetPassword(newPassword: string): Observable<any> {
    const token = localStorage.getItem(SignUpStorageKey.OTP_TOKEN);
    const emailId = localStorage.getItem(SignUpStorageKey.EMAIL_ID);
    return this.http.post(
      USER_MANAGEMENT_URL + '/auth/reset-password',
      {
        "emailId": emailId,
        "token": token,
        "newPassword": newPassword,
        "app": this.protal_app
      },
      httpOptions
    );
  }

  getUserProfile(): Observable<any> {
    const token = this.storageService.getToken();
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + token,
    });

    return this.http.get(
      USER_MANAGEMENT_URL + '/developer-portal/profile',
      { headers }
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<HttpResponse<any>> {
    const token = localStorage.getItem('logged-user');
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + token,
    });

    return this.http.post(
      USER_MANAGEMENT_URL + '/auth/change-password',
      {
        "currentPassword": currentPassword,
        "newPassword": newPassword
      },
      { headers, observe: 'response' }
    );
  }

  maskEmail(email: string): string {
    const [localPart, domain] = email.split('@');
    if (!domain) return email;

    const firstTwo = localPart.substring(0, 2);
    return `${firstTwo}****@${domain}`;
  }

  // Example: Logout
  logout(): void {
    localStorage.clear();
    this.router.navigate(['/auth/login']);
  }

  // Save token
  saveToken(token: string): void {
    localStorage.setItem('token', token);
  }

  // Get token
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  // Check if logged in
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  refreshAccessToken(userData: any): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + userData.accessToken,
      'x-refresh-token': userData.refreshToken
    });
    return this.http.get(`${AUTHORIZER_URL}/refresh`, { headers });
  }

  fetchMenu(token: any): Observable<any> {

    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + token,
    });

    return this.http.get(
      USER_MANAGEMENT_URL + '/auth/menu',
      { headers }
    )

  }

  tfa_qr_generation(emailId:any,reset:Boolean): Observable<any> {
    return this.http.post(`${USER_MANAGEMENT_URL}/auth/tfa/setup`,
      { 
        "userName" : emailId,
        "app" : this.protal_app,
        "reset" : reset
     },{ responseType: 'blob' });
  }

  // Verify 2FA code
  tfa_otp_verification(emailId:any,otp:any): Observable<any> {
    return this.http.post(`${USER_MANAGEMENT_URL}/auth/tfa/verify`, 
      { 
        "userName" : emailId,
        "app" : this.protal_app,
        "otp": otp
     });
  }

  testWebhookNotification(webhookUrl: string, body: object): Observable<HttpResponse<string>> {
    const apiUrl = `${DEV_PORTAL_URL}/developer-portal/webhook/test-webhook-notification?webhookUrl=${encodeURIComponent(webhookUrl)}`;
    return this.http.post(apiUrl, body, { observe: 'response', responseType: 'text' });
  }


  getRole(): string {
    const loggedUser = JSON.parse(localStorage.getItem('logged-user') || '{}');
    return loggedUser.accessToken
      ? jwtDecode<JwtPayload>(loggedUser.accessToken).role_type
      : '';
  }

  private getDefaultHeaders(authRequired: boolean): HttpHeaders {
    console.log('Adding header for AUTH service')
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (authRequired) {
      const token = localStorage.getItem('logged-user');
      if (token) {
        headers = headers.set('Authorization', `${this.storageService.getToken()}`);
      }
    }

    return headers;
  }

}
